import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Trophy,
  AlertCircle,
  CheckCircle,
  X,
  Wallet,
  Receipt,
  Users
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const EventLiquidation = ({ eventId, event }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [liquidationAmount, setLiquidationAmount] = useState('');
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  const liquidateMutation = useMutation(
    ({ userId, amount }) =>
      api.post(`/events/${eventId}/liquidate`, { userId, amount }),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('events');
        toast.success(response.data.message || 'Liquidación procesada exitosamente');
        setShowModal(false);
        setSelectedClient(null);
        setLiquidationAmount('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al procesar liquidación');
      }
    }
  );

  const handleOpenModal = (participant) => {
    setSelectedClient(participant);
    setLiquidationAmount(participant.current_balance || '0');
    setShowModal(true);
  };

  const handleLiquidate = () => {
    if (!selectedClient) return;

    const amount = parseFloat(liquidationAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    if (amount > selectedClient.current_balance) {
      toast.error('El monto excede el saldo disponible');
      return;
    }

    if (window.confirm(
      `¿Confirmar liquidación de $${amount.toLocaleString()} a ${selectedClient.full_name}?\n\nEsto retirará el monto del saldo del cliente.`
    )) {
      liquidateMutation.mutate({
        userId: selectedClient.user_id,
        amount
      });
    }
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const calculateClientStats = (participant) => {
    // Calcular estadísticas del cliente en este evento
    const currentBalance = Number(participant.current_balance || 0);
    const seedAmount = Number(participant.seed_amount || 0);
    const initialCapital = Number(participant.initial_capital || 0);

    // Ganancia/Pérdida neta
    const netProfit = currentBalance - initialCapital;
    const isProfitable = netProfit > 0;

    return {
      currentBalance,
      seedAmount,
      initialCapital,
      netProfit,
      isProfitable,
      profitPercentage: initialCapital > 0 ? (netProfit / initialCapital) * 100 : 0
    };
  };

  if (!event || event.status !== 'completed') {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="w-12 h-12 text-warning-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Liquidación no disponible
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          La liquidación solo está disponible para eventos completados
        </p>
      </div>
    );
  }

  const participants = event.participants || [];
  const activeParticipants = participants.filter(p => p.is_active || p.participated);

  if (activeParticipants.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Sin participantes
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          No hay participantes en este evento para liquidar
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-primary rounded-lg">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Liquidación de Clientes
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Procesar retiros y pagos a clientes del evento
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total participantes
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {activeParticipants.length}
            </p>
          </div>
        </div>

        {/* Resumen del Evento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Evento</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{event.name}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Comisión</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              {event.commission_rate || 0}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Pasos Completados</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              {event.current_step || 0} / {event.max_steps || 13}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Clientes */}
      <div className="space-y-4">
        {activeParticipants.map((participant, index) => {
          const stats = calculateClientStats(participant);

          return (
            <motion.div
              key={participant.user_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card p-6"
            >
              <div className="flex flex-col md:flex-row items-start justify-between gap-4 md:gap-6">
                {/* Info del Cliente */}
                <div className="flex-1 w-full">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {participant.full_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {participant.full_name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {participant.email}
                      </p>
                    </div>
                  </div>

                  {/* Estadísticas Financieras */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <Wallet className="w-4 h-4 text-gray-400" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Saldo Actual
                        </p>
                      </div>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(stats.currentBalance)}
                      </p>
                    </div>

                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Capital Inicial
                        </p>
                      </div>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(stats.initialCapital)}
                      </p>
                    </div>

                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        {stats.isProfitable ? (
                          <TrendingUp className="w-4 h-4 text-success-500" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-danger-500" />
                        )}
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {stats.isProfitable ? 'Ganancia' : 'Pérdida'}
                        </p>
                      </div>
                      <p className={`text-lg font-bold ${
                        stats.isProfitable
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}>
                        {formatCurrency(Math.abs(stats.netProfit))}
                      </p>
                    </div>

                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-1">
                        <Trophy className="w-4 h-4 text-gray-400" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          ROI
                        </p>
                      </div>
                      <p className={`text-lg font-bold ${
                        stats.isProfitable
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}>
                        {stats.profitPercentage.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Botón de Liquidación */}
                <div className="md:ml-6 w-full md:w-auto">
                  <button
                    onClick={() => handleOpenModal(participant)}
                    disabled={stats.currentBalance <= 0}
                    className={`btn-primary ${
                      stats.currentBalance <= 0
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Liquidar
                  </button>
                  {stats.currentBalance <= 0 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Sin saldo
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Modal de Liquidación */}
      <AnimatePresence>
        {showModal && selectedClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-900 rounded-xl shadow-xl max-w-md w-full p-6"
            >
              {/* Header del Modal */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Procesar Liquidación
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Info del Cliente */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Cliente</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {selectedClient.full_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedClient.email}
                </p>
              </div>

              {/* Saldo Disponible */}
              <div className="mb-6">
                <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                  <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    Saldo Disponible
                  </span>
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(selectedClient.current_balance)}
                  </span>
                </div>
              </div>

              {/* Input de Monto */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Monto a Liquidar
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="number"
                    value={liquidationAmount}
                    onChange={(e) => setLiquidationAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                    min="0"
                    max={selectedClient.current_balance}
                    step="1000"
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <button
                    onClick={() => setLiquidationAmount((selectedClient.current_balance * 0.5).toFixed(2))}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    50%
                  </button>
                  <button
                    onClick={() => setLiquidationAmount(selectedClient.current_balance)}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    100% (Todo)
                  </button>
                </div>
              </div>

              {/* Advertencia */}
              <div className="mb-6 p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-warning-800 dark:text-warning-200">
                    <p className="font-medium mb-1">Importante:</p>
                    <p>
                      Este monto será descontado del saldo del cliente por concepto de liquidación.
                      Esta acción no se puede deshacer.
                    </p>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 btn-secondary"
                  disabled={liquidateMutation.isLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleLiquidate}
                  disabled={liquidateMutation.isLoading || !liquidationAmount || parseFloat(liquidationAmount) <= 0}
                  className="flex-1 btn-primary"
                >
                  {liquidateMutation.isLoading ? (
                    <>
                      <div className="spinner w-4 h-4 mr-2"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirmar Liquidación
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventLiquidation;
